import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import * as Routes from '@/modules/routes';
import { Link } from 'react-router-dom';
import cn from '@/utils/cn';
import { lessonGuidesPath, lessonSectionsPath, useContentMount } from '../contentMount';
import LessonActionsMenu from './LessonActionsMenu';
import NumberCell from './NumberCell';
import SharedProgramsCell from './SharedProgramsCell';
import StateViewCell from './StateViewCell';
import { ProgramLesson } from './types';

const columnHelper = createColumnHelper<ProgramLesson>();

const linkClass = cn(
  'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover',
  'tw-no-underline tw-transition-colors',
);

interface CountLinkProps {
  count: number;
  href: string;
}

const CountLink = ({ count, href }: CountLinkProps) => (
  <a className={linkClass} href={href}>{count}</a>
);

interface TitleLinkProps {
  children: React.ReactNode;
  lesson: ProgramLesson;
  programId: number;
}

// The title opens the lesson's home, the SPA sections page, on either mount.
const TitleLink = ({ children, lesson, programId }: TitleLinkProps) => (
  <Link className={linkClass} to={lessonSectionsPath(programId, lesson.id, useContentMount())}>
    {children}
  </Link>
);

// The slideshow count opens the SPA lesson guides page, on either mount.
const SlideShowsCountCell = ({ lesson, programId }: { lesson: ProgramLesson; programId: number }) => (
  <Link className={linkClass} to={lessonGuidesPath(programId, lesson.id, useContentMount())}>
    {lesson.slide_shows_count}
  </Link>
);

const getLessonColumns = (programId: number) => [
  columnHelper.accessor('title_number', {
    cell: info => <NumberCell lesson={info.row.original} programId={programId} />,
    enableSorting: false,
    header: '#',
    size: 100,
  }),
  columnHelper.accessor('title', {
    cell: (info) => {
      const { kind } = info.row.original;
      const kindLabel = kind && kind !== 'default'
        ? `[${kind.replace(/\b\w/g, c => c.toUpperCase())}] `
        : '';

      return (
        <TitleLink lesson={info.row.original} programId={programId}>
          {kindLabel && <span className="tw-text-spa-secondary">{kindLabel}</span>}
          {info.getValue()}
        </TitleLink>
      );
    },
    enableSorting: false,
    header: 'Title',
    size: 350,
  }),
  columnHelper.display({
    cell: info => <StateViewCell lesson={info.row.original} />,
    enableSorting: false,
    header: 'State View',
    id: 'state_view',
    meta: { align: 'center' },
    size: 110,
  }),
  columnHelper.accessor('materials_count', {
    cell: info => (
      <CountLink
        count={info.getValue()}
        href={Routes.staffer_lesson_materials_path(info.row.original.id, { program_id: programId })}
      />
    ),
    enableSorting: false,
    header: 'Materials',
    id: 'materials',
    meta: { align: 'center' },
    size: 90,
  }),
  columnHelper.accessor('slide_shows_count', {
    cell: info => <SlideShowsCountCell lesson={info.row.original} programId={programId} />,
    enableSorting: false,
    header: 'Slideshows',
    id: 'slideshows',
    meta: { align: 'center' },
    size: 90,
  }),
  columnHelper.accessor('games_count', {
    cell: info => (
      <CountLink
        count={info.getValue()}
        href={Routes.admin_games_path(info.row.original.id, { program_id: programId })}
      />
    ),
    enableSorting: false,
    header: 'Games',
    id: 'games',
    meta: { align: 'center' },
    size: 80,
  }),
  columnHelper.accessor('notebooks_count', {
    cell: info => (
      <CountLink
        count={info.getValue()}
        href={Routes.lesson_index_admin_notebooks_path({
          lesson_id: info.row.original.id,
          program_id: programId,
        })}
      />
    ),
    enableSorting: false,
    header: 'Notebooks',
    id: 'notebooks',
    meta: { align: 'center' },
    size: 90,
  }),
  columnHelper.display({
    cell: info => <SharedProgramsCell lesson={info.row.original} programId={programId} />,
    header: 'Shared Programs',
    id: 'shared_programs',
    meta: { align: 'center' },
    size: 90,
  }),
  columnHelper.display({
    cell: info => <LessonActionsMenu lesson={info.row.original} programId={programId} />,
    enableSorting: false,
    header: 'Action',
    id: 'action',
    meta: { align: 'center' },
    size: 110,
  }),
];

export default getLessonColumns;
